import React, { useCallback, useState, useEffect, useRef, ChangeEvent, useMemo } from "react";
import MuiAlert from '@mui/material/Alert';
import { ThemeProvider, Button, Box, Snackbar, SelectChangeEvent, Grid, CircularProgress } from "@mui/material";
import colorTheme from "../../assets/colorTheme";
import "../../assets/style.css";
import { useActions } from "../../hooks/useActions";
import PluginService from "../../services/pluginService";
import IIntegration from "../../models/configuration/servers/IIntegration";
import EncompassService from "../../services/encompassService";
import { useSelector } from "react-redux";
import { RootState, store } from "../../state/store";
import ConfigLoad from "../../components/shared/config/ConfigLoad";
import { IImpersonation } from "../../models/configuration/plugin/IConfiguration";
import { ConfigurationDataType, useConfigurationData } from "../../hooks/useConfigurationData";
import { useConfigurationSave } from "../../hooks/useConfigurationSave";
import { setClientCode, setServerUris, setConfigId } from "../../state/appSlice";
import { AlertSeverity } from "../../constants/AlertTypes";
import ConformXService from "../../services/conformXService";
import DtTabs from "../../components/ui/tab/DtTabs";
import { DtCard } from "../../components/ui/card";
import DtForm from "../../components/ui/form/DtForm";
import { DtPageHeader } from "../../components/ui/header";
import DtAlert from "../../components/ui/alert/DtAlert";
import { validateUrl } from "../../utils/apiHelper";
import { DtLoader } from "../../components/ui/loader/DtLoader";
import { hasPluginConfigChanges } from "../../utils/pluginConfigChangeDetector";
import { hasXPathConfigChanges } from "../../utils/xpathConfigChangeDetector";
import { hasPushbackConfigChanges } from "../../utils/pushbackConfigChangeDetector";
import { hasDocumentBundlingChanges, hasEventBundlingChanges } from "../../utils/bundlingConfigChangeDetector";
import { consumeGlobalSaveHadChanges } from "../../utils/globalSaveStatus";


const ENCOMPASS_INT_API_URI = process.env.REACT_APP_INTEGRATION_API;
const ENCOMPASS_PLUGIN_API_URI = process.env.REACT_APP_DT_PLUGIN_API_URI;
const ENCOMPASS_PUSHBACK_API_URI = process.env.REACT_APP_DT_PUSHBACK_API_URI;
const ENCOMPASS_TOKEN_ENDPOINT_API_URI = process.env.REACT_APP_DT_TOKEN_ENDPOINT_API_URI;

// URI field name constants
const URI_FIELD_NAMES = {
    INTEGRATION_URI: 'integrationUri',
    PLUGIN_URI: 'pluginUri',
    PUSHBACK_URI: 'pushbackUri',
    TOKEN_URI: 'tokenUri'
} as const;

const ConfigServers: React.FC = () => {
    interface IConfigurationIds { ConfigurationIds: string[] };

    // Reducers
    const {
        saveConfigServerData,
        saveConfigImpersonationData,
        saveConfigPlugInData } = useActions();
    const accessToken = useSelector((state: RootState) => state.appSlice.accessToken);
    const { data: pluginConfiguration, persistedData: persistedPluginConfiguration } = useSelector((state: RootState) => state.configPlugInData);
    const xPathData = useSelector((state: RootState) => state.xPathData.data);
    const persistedXPathData = useSelector((state: RootState) => state.xPathData.persistedData);
    const pushbackData = useSelector((state: RootState) => state.configPushbackDefaults.data);
    const persistedPushbackData = useSelector((state: RootState) => state.configPushbackDefaults.persistedData);
    const documentBundles = useSelector((state: RootState) => state.configDocumentBundles.data);
    const persistedDocumentBundles = useSelector((state: RootState) => state.configDocumentBundles.persistedData);
    const eventBundles = useSelector((state: RootState) => state.configEventBundles.data);
    const persistedEventBundles = useSelector((state: RootState) => state.configEventBundles.persistedData);
    const configData = useConfigurationData(ConfigurationDataType.IMPERSONATION);
    const [integrationData, setIntegrationData] = useState<IIntegration | null>({
        ConformX: {
            ApiEndpointUrl: '',
            ApiKey: '',
            ApiSecret: ''
        },
        Encompass: {
            Username: '',
            Password: ''
        },
        MaxConcurrency: 0,
        BaseUrl: null
    });
    const [impersonationDataState, setImpersonationDataState] = useState<IImpersonation | null>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [disableParentLoader, setDisableParentLoader] = useState<boolean>(false);
    const [tabIndex, setTabIndex] = useState(0);

    // State
    const [configIds, setConfigIds] = useState<string[]>([]);
    const [configIdsLoading, setConfigIdsLoading] = useState<boolean>(true);
    const [selectedConfigId, setSelectedConfigId] = useState<string>('');
    const [integrationUri, setIntegrationUri] = useState('');
    const [pluginUri, setPluginUri] = useState('');
    const [pushbackUri, setPushbackUri] = useState('');
    const [tokenUri, setTokenUri] = useState('');
    const [changedFields, setChangedFields] = useState<{ [key: string]: any }>({}); // Track only changed fields

    // State Confirmations
    const [alertOpen, setAlertOpen] = useState(false);
    const [saveError, setSaveError] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');
    const [errorMessage, setErrorMessage] = useState<string>('');

    // Refs
    const hasFetchedData = useRef(false);
    const hasFetchedConfigIds = useRef(false);

    // Services
    const pluginService = useMemo(() => new PluginService(), []);
    const { data: impersonationData, error: impersonationError } = configData
    const impersonationMemoData = useMemo(() => impersonationData, [impersonationData]);


    // Save ConformX configuration data
    const pushConfigServerData = useCallback(async (configId: string, conformXData: any) => {
        // Create simple payload with ConformX fields and empty Encompass object
        const payload = {
            ConformX: conformXData,
            Encompass: {}
        } as IIntegration;

        await saveConfigServerData(configId, payload, accessToken);
    }, [saveConfigServerData, accessToken]);

    const fetchConfigIds = useCallback(async () => {
        const cId = getConfigId();
        if (!cId) {
            console.error("No valid configuration ID found.");
            setConfigIdsLoading(false);
            return;
        }

        try {
            setConfigIdsLoading(true);
            const response = await pluginService.getConfig<IConfigurationIds>(accessToken, "ids", cId);
            if (response && response.ConfigurationIds) {
                setConfigIds(response.ConfigurationIds);
            }
        } catch (error) {
            console.error("Error fetching configuration IDs:", error);
        } finally {
            setConfigIdsLoading(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pluginService, accessToken]);

    // Mount control
    useEffect(() => {
        if (!hasFetchedData.current) {
            const configId = getConfigId();
            setSelectedConfigId(configId);
            initializeScreenData(); // Initialize screen data immediately
            hasFetchedData.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle Impersonation Data
    useEffect(() => {
        if (impersonationMemoData != null) {
            setImpersonationDataState(impersonationMemoData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [impersonationMemoData]);

    useEffect(() => {
        if (!impersonationDataState) {
            return;
        }

        if (!impersonationDataState.EnableImpersonation) {
            setConfigIds([]);
            setConfigIdsLoading(false);
            hasFetchedConfigIds.current = false;
            return;
        }

        if (!hasFetchedConfigIds.current) {
            const result = fetchConfigIds();

            // If fetchConfigIds returns a Promise, only mark as fetched on success.
            if (result && typeof (result as any).then === "function") {
                (result as Promise<unknown>)
                    .then(() => {
                        hasFetchedConfigIds.current = true;
                    })
                    .catch(() => {
                        // Allow retry on next effect run if the fetch failed.
                        hasFetchedConfigIds.current = false;
                    });
            } else {
                // For synchronous implementations, preserve existing behavior.
                hasFetchedConfigIds.current = true;
            }
        }
    }, [impersonationDataState, fetchConfigIds]);

    function initializeScreenData() {
        const integrationUri = localStorage.getItem(URI_FIELD_NAMES.INTEGRATION_URI) || ENCOMPASS_INT_API_URI as string;
        const pluginUri = localStorage.getItem(URI_FIELD_NAMES.PLUGIN_URI) || ENCOMPASS_PLUGIN_API_URI as string;
        const pushbackUri = localStorage.getItem(URI_FIELD_NAMES.PUSHBACK_URI) || ENCOMPASS_PUSHBACK_API_URI as string;
        const tokenUri = localStorage.getItem(URI_FIELD_NAMES.TOKEN_URI) || ENCOMPASS_TOKEN_ENDPOINT_API_URI as string;
        setIntegrationUri(integrationUri);
        setPluginUri(pluginUri);
        setPushbackUri(pushbackUri);
        setTokenUri(tokenUri);
        store.dispatch(setServerUris({
            encompassRequestUri: integrationUri,
            encompassPluginUri: pluginUri,
            encompassPushbackUri: pushbackUri,
            encompassTokenUri: tokenUri
        }));
        if (!selectedConfigId) {
            setSelectedConfigId(getConfigId())
        }
    }

    // Helper function to build ConformX update with current field values
    const buildPartialUpdate = () => {
        const conformXUpdate: any = {};

        // Simply get current values from integrationData ConformX fields
        if (integrationData?.ConformX?.ApiEndpointUrl) {
            conformXUpdate.ApiEndpointUrl = integrationData.ConformX.ApiEndpointUrl;
        }
        if (integrationData?.ConformX?.ApiKey) {
            conformXUpdate.ApiKey = integrationData.ConformX.ApiKey;
        }
        if (integrationData?.ConformX?.ApiSecret) {
            conformXUpdate.ApiSecret = integrationData?.ConformX?.ApiSecret;
        }

        return conformXUpdate;
    };

    const save = async () => {
        const globalSaved = consumeGlobalSaveHadChanges();
        const pluginHasChanges = pluginConfiguration?.Configuration
            ? hasPluginConfigChanges(
                pluginConfiguration.Configuration,
                persistedPluginConfiguration?.Configuration
            )
            : false;
        const hasXPathChanges = xPathData ? hasXPathConfigChanges(xPathData, persistedXPathData) : false;
        const hasPushbackChanges = pushbackData ? hasPushbackConfigChanges(pushbackData, persistedPushbackData) : false;
        const hasDocumentBundlingChange = documentBundles ? hasDocumentBundlingChanges(documentBundles, persistedDocumentBundles) : false;
        const hasEventBundlingChange = eventBundles ? hasEventBundlingChanges(eventBundles, persistedEventBundles) : false;
        const hasAnyChanges = Object.keys(changedFields).length > 0 || pluginHasChanges || hasXPathChanges || hasPushbackChanges || hasDocumentBundlingChange || hasEventBundlingChange || globalSaved;
        if (!hasAnyChanges) {
            openAlert("No new changes to save", "info");
            setSaveError(false);
            return;
        }

        const hasConformXChanges = [
            "ConformX.ApiEndpointUrl",
            "ConformX.ApiKey",
            "ConformX.ApiSecret"
        ].some(field => Object.prototype.hasOwnProperty.call(changedFields, field));
        const hasImpersonationChanges = Object.prototype.hasOwnProperty.call(changedFields, "Impersonation.ClientCode");

        try {
            setSaving(true);

            // Check if there are URI changes to cache
            const hasUriChanges = [URI_FIELD_NAMES.INTEGRATION_URI, URI_FIELD_NAMES.PLUGIN_URI, URI_FIELD_NAMES.PUSHBACK_URI, URI_FIELD_NAMES.TOKEN_URI]
                .some(uriField => changedFields.hasOwnProperty(uriField));

            if (hasUriChanges) {
                cacheEndpoints();
            }

            // Build ConformX data to save
            const conformXUpdate = hasConformXChanges ? buildPartialUpdate() : {};

            // Save ConformX configuration if there are any values
            if (Object.keys(conformXUpdate).length > 0) {
                console.log(getConfigId(), conformXUpdate)
                await pushConfigServerData(getConfigId(), conformXUpdate);
            }

            // Save impersonation data if changed
            if (hasImpersonationChanges) {
                await updateImpersonationData();
            }

            if (pluginHasChanges && pluginConfiguration) {
                await saveConfigPlugInData(getConfigId(), pluginConfiguration, accessToken);
            }

            openAlert("Successfully Saved Changes", "success");
            setSaveError(false);

            // Clear changed fields after successful save
            setChangedFields({});
        } catch (e) {
            console.error("Error saving data", e);
            setSaveError(true);
            const errorMessage = e instanceof Error ? e.message : "Failed to save changes";
            openAlert(errorMessage, "error");
        } finally {
            setSaving(false);
        }
    }

    // Register save function for header buttons
    useConfigurationSave(() => save());

    const [testingConnection, setTestingConnection] = useState<boolean>(false);

    async function TestConformXConnection(ApiEndpointUrl: any): Promise<void> {
        try {
            setTestingConnection(true);

            const conformXService = new ConformXService(`${ApiEndpointUrl}`);
            const result = await conformXService.isAuthenticated(
                integrationData?.ConformX?.ApiKey as string,
                integrationData?.ConformX?.ApiSecret as string);

            if (!result.success) {
                throw new Error(result.errorMessage || `Something went wrong, please try again later or contact support.`);
            }

            ResetErrors();
            openAlert("Connection successful", "success");
        } catch (error) {
            console.log(error);
            openAlert((error as Error).message, "error");
        } finally {
            setTestingConnection(false); // Stop loading
        }
    }

    const ResetErrors = () => {
        setErrorMessage('');
        setSaveError(false);
    }

    const updateImpersonationData = async () => {
        if (!impersonationDataState) return;
        await saveConfigImpersonationData(getConfigId(), {
            ClientCode: impersonationDataState.ClientCode,
            EnableImpersonation: impersonationDataState.EnableImpersonation
        }, accessToken);
    };

    useEffect(() => {
        if (selectedConfigId) {
            localStorage.setItem('configurationId', selectedConfigId);
        }
    }, [selectedConfigId]);


    const handleConfigChange = (event: SelectChangeEvent) => {
        const newConfigId = event.target.value as string;
        setSelectedConfigId(newConfigId);
        store.dispatch(setConfigId(newConfigId));
    };


    const getConfigId = () => {
        if (selectedConfigId)
            return selectedConfigId;
        return EncompassService.getConfigId() as string;
    }

    useEffect(() => {
        if (integrationUri || pluginUri || tokenUri) {
            cacheEndpoints();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [integrationUri, pluginUri, tokenUri]);

    function cacheEndpoints() {
        localStorage.setItem(URI_FIELD_NAMES.INTEGRATION_URI, integrationUri);
        localStorage.setItem(URI_FIELD_NAMES.PLUGIN_URI, pluginUri);
        localStorage.setItem(URI_FIELD_NAMES.PUSHBACK_URI, pushbackUri);
        localStorage.setItem(URI_FIELD_NAMES.TOKEN_URI, tokenUri);
    }

    // Handle Changes to local state based elements using input name as the key
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        // Track changed fields
        setChangedFields(prev => ({
            ...prev,
            [name]: value
        }));

        setIntegrationData((prevState) => {
            if (!prevState) {
                // Initialize with default structure if null
                prevState = {
                    ConformX: {
                        ApiEndpointUrl: '',
                        ApiKey: '',
                        ApiSecret: ''
                    },
                    Encompass: {
                        Username: '',
                        Password: ''
                    },
                    MaxConcurrency: 0,
                    BaseUrl: null
                };
            }

            const keys = name.split('.');
            const newState = { ...prevState };
            let currentLevel: any = newState;

            // Iterate through the value using the name of the inputs
            for (let i = 0; i < keys.length - 1; i++) {
                currentLevel[keys[i] as keyof typeof currentLevel] = {
                    ...currentLevel[keys[i] as keyof typeof currentLevel],
                };
                currentLevel = currentLevel[keys[i] as keyof typeof currentLevel];
            }
            currentLevel[keys[keys.length - 1] as keyof typeof currentLevel] = value;
            return newState;
        });
    };

    const handleClientCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
        const rawValue = event.target.value;
        const nextClientCode = rawValue.trim() === "" ? null : rawValue;
        setImpersonationDataState({
            ...impersonationDataState,
            ClientCode: nextClientCode,
            EnableImpersonation: impersonationDataState?.EnableImpersonation ?? false
        });
        store.dispatch(setClientCode(rawValue));
        setChangedFields(prev => ({
            ...prev,
            "Impersonation.ClientCode": nextClientCode
        }));
    }

    const handleUriChange = (
        setter: React.Dispatch<React.SetStateAction<string>>,
        fieldName: string
    ) => (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target?.value;
        setter(value);

        // Track that this URI field has changed
        setChangedFields(prev => ({
            ...prev,
            [fieldName]: value
        }));
    }

    // Alert Handling
    const handleSnackBarClose = (_event: any) => { setAlertOpen(false); }
    const openAlert = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
        setAlertSeverity(severity);
        setAlertMessage(message);
        setAlertOpen(true);
    }

    return (
        <ThemeProvider theme={colorTheme}>
            <section>
                <div>
                    {saving && !disableParentLoader && <ConfigLoad loading={saving} />}
                </div>
                <div>
                    <DtPageHeader divider={false}>Server Configurations</DtPageHeader>
                    <Box
                        mt={1}
                        component="main"
                        sx={{
                        }}
                    >
                        <DtTabs
                            tabs={[
                                { label: "ConformX Server Configuration" },
                                { label: "Enterprise Plugin Configuration" }
                            ]}
                            value={tabIndex}
                            onChange={(_, idx) => setTabIndex(idx)}
                            borderBottom
                        >
                            {/* Tab 1: ConformX Server Configuration */}
                            {configIdsLoading ? (
                                <Grid container spacing={1} sx={{ width: "100%", margin: "0 auto", display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                                    <DtLoader message="Loading configuration IDs..." />
                                </Grid>
                            ) : (
                                <Grid container spacing={1} sx={{ width: "100%", margin: "0 auto" }}>
                                    <Grid item xs={12} md={12} lg={8}>
                                        <DtAlert color='info'>
                                            Previously entered config details were saved. Please enter the credentials again to perform Test Connection or to define new ConformX Server Configuration.
                                        </DtAlert>
                                    </Grid>
                                    <Grid item xs={12} md={12} lg={8}>
                                    <DtForm
                                        fields={[
                                            {
                                                id: "clientCode",
                                                label: "Client Code",
                                                type: "text",
                                                defaultValue: impersonationDataState?.ClientCode || '',
                                                required: true,
                                                onChange: (value: string) => handleClientCodeChange({
                                                    target: {
                                                        value
                                                    }
                                                } as ChangeEvent<HTMLInputElement>),
                                                visibility: !!impersonationDataState?.EnableImpersonation,
                                                size: 4
                                            },
                                            {
                                                id: "configurationIdDropdown",
                                                label: "Configuration ID",
                                                type: "select",
                                                options: configIds.map(id => ({ label: id, value: id })),
                                                defaultValue: selectedConfigId,
                                                required: true,
                                                onChange: (value: string) => handleConfigChange({
                                                    target: {
                                                        value
                                                    }
                                                } as SelectChangeEvent),
                                                visibility: !!impersonationDataState?.EnableImpersonation,
                                                size: 8
                                            },
                                            {
                                                id: "conformxApiEndpointUrl",
                                                label: "ConformX API URL",
                                                type: "text",
                                                defaultValue: integrationData?.ConformX?.ApiEndpointUrl || '',
                                                required: true,
                                                onChange: (value: string) => handleChange({
                                                    target: {
                                                        name: "ConformX.ApiEndpointUrl",
                                                        value
                                                    }
                                                } as ChangeEvent<HTMLInputElement>),
                                                validate: (value: string) => validateUrl(value, false),
                                                showValidationStatusIcon: true
                                            },
                                            {
                                                id: "conformxApiKey",
                                                label: "API Key ID",
                                                type: "text",
                                                defaultValue: integrationData?.ConformX?.ApiKey || '',
                                                required: true,
                                                onChange: (value: string) => handleChange({
                                                    target: {
                                                        name: "ConformX.ApiKey",
                                                        value
                                                    }
                                                } as ChangeEvent<HTMLInputElement>),
                                                size: 6,
                                            },
                                            {
                                                id: "conformxApiSecret",
                                                label: "API Key Secret",
                                                type: "password",
                                                defaultValue: integrationData?.ConformX?.ApiSecret || '',
                                                required: true,
                                                onChange: (value: string) => handleChange({
                                                    target: {
                                                        name: "ConformX.ApiSecret",
                                                        value
                                                    }
                                                } as ChangeEvent<HTMLInputElement>),
                                                size: 6
                                            },
                                        ]}
                                        values={{
                                            clientCode: impersonationDataState?.ClientCode,
                                            configurationIdDropdown: selectedConfigId,
                                            conformxApiEndpointUrl: integrationData?.ConformX?.ApiEndpointUrl,
                                            conformxApiKey: integrationData?.ConformX?.ApiKey,
                                            conformxApiSecret: integrationData?.ConformX?.ApiSecret
                                        }}
                                        onSubmit={() => { }}
                                        showSubmit={false}
                                        showReset={false}
                                    />
                                    <Box sx={{ marginTop: "1rem" }}>
                                        <Button
                                            onClick={() => TestConformXConnection(integrationData?.ConformX?.ApiEndpointUrl || "")}
                                            variant="contained"
                                            size="small"
                                            disabled={
                                                testingConnection ||
                                                !integrationData?.ConformX?.ApiEndpointUrl ||
                                                !integrationData?.ConformX?.ApiKey ||
                                                !integrationData?.ConformX?.ApiSecret
                                            }
                                            startIcon={testingConnection ? (
                                                <CircularProgress size={16} sx={{ color: 'white' }} />
                                            ) : undefined}
                                        >
                                            {testingConnection ? "Testing..." : "Test Connection"}
                                        </Button>
                                    </Box>
                                </Grid>
                                </Grid>
                            )}

                            {/* Tab 2: Enterprise Plugin Configuration */}
                            <Grid container spacing={0.5} sx={{ width: "100%", margin: "0 auto" }}>
                                <Grid item xs={12} md={12} lg={8} xl={6}>
                                   <Box sx={{ maxHeight: '65vh'}}>
                                        <DtForm
                                            fields={[
                                                {
                                                    id: "configurationId",
                                                    label: "Configuration ID",
                                                    type: "text",
                                                    defaultValue: selectedConfigId,
                                                    readOnly: true,
                                                    disabled: true,
                                                },
                                                {
                                                    id: "integrationUri",
                                                    label: "Request API URI",
                                                    type: "text",
                                                    defaultValue: integrationUri,
                                                    required: true,
                                                    onChange: (value: string) => {
                                                        if (typeof value !== "undefined") {
                                                            handleUriChange(setIntegrationUri, URI_FIELD_NAMES.INTEGRATION_URI)({
                                                                target: { value }
                                                            } as ChangeEvent<HTMLInputElement>);
                                                        }
                                                    },
                                                    validate: (value: string) => validateUrl(value, false),
                                                    showValidationStatusIcon: true
                                                },
                                                {
                                                    id: "pluginUri",
                                                    label: "Plugin API URI",
                                                    type: "text",
                                                    defaultValue: pluginUri,
                                                    required: true,
                                                    onChange: (value: string) => {
                                                        if (typeof value !== "undefined") {
                                                            handleUriChange(setPluginUri, URI_FIELD_NAMES.PLUGIN_URI)({
                                                                target: { value }
                                                            } as ChangeEvent<HTMLInputElement>);
                                                        }
                                                    },
                                                    validate: (value: string) => validateUrl(value, false),
                                                    showValidationStatusIcon: true
                                                },
                                                {
                                                    id: "pushbackUri",
                                                    label: "Pushback API URI",
                                                    type: "text",
                                                    defaultValue: pushbackUri,
                                                    required: true,
                                                    onChange: (value: string) => {
                                                        if (typeof value !== "undefined") {
                                                            handleUriChange(setPushbackUri, URI_FIELD_NAMES.PUSHBACK_URI)({
                                                                target: { value }
                                                            } as ChangeEvent<HTMLInputElement>);
                                                        }
                                                    },
                                                    validate: (value: string) => validateUrl(value, false),
                                                    showValidationStatusIcon: true
                                                },
                                                {
                                                    id: "tokenUri",
                                                    label: "Token Endpoint URI",
                                                    type: "text",
                                                    defaultValue: tokenUri,
                                                    required: true,
                                                    onChange: (value: string) => {
                                                        if (typeof value !== "undefined") {
                                                            handleUriChange(setTokenUri, URI_FIELD_NAMES.TOKEN_URI)({
                                                                target: { value }
                                                            } as ChangeEvent<HTMLInputElement>);
                                                        }
                                                    },
                                                    validate: (value: string) => validateUrl(value, false),
                                                    showValidationStatusIcon: true
                                                },
                                            ]}
                                            values={{ configurationId: selectedConfigId, integrationUri, pluginUri, pushbackUri, tokenUri }}
                                            onSubmit={() => { }}
                                            showSubmit={false}
                                            showReset={false}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </DtTabs>

                        <Snackbar
                            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                            open={alertOpen}
                            autoHideDuration={8000}
                            onClose={handleSnackBarClose}
                        >
                            <MuiAlert
                                onClose={handleSnackBarClose}
                                severity={alertSeverity}
                                sx={{ width: '100%', fontSize: '1.2rem', padding: '12px 16px' }}>
                                {alertMessage}
                            </MuiAlert>
                        </Snackbar>
                    </Box>
                </div>
            </section>
        </ThemeProvider >
    )
}

export default ConfigServers;